import { type Customer } from '@einwert/valuation-api-client'
import { ForbiddenError } from '@einwert/valuation-api-client'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import { Outlet, ScrollRestoration, useNavigate } from 'react-router-dom'
import { SelectedCustomerProvider } from './context/SelectedCustomerContext'
import { IAmContext } from './context/IAmContext'
import { useGetCurrentUser } from 'ClientPortal/src/api/Queries/users'
import { ReactNode, Suspense, useEffect } from 'react'
import type { RootUser as User } from '@einwert/valuation-api-client'
import * as Sentry from '@sentry/browser'
import { getEnv, getFrontendVersion } from 'ComponentLibrary/src/common/environment'
import {
    ErrorSnackbar,
    ErrorSnackbarProvider,
    sendToSentry,
    SERVICES_NEEDING_CONSENT,
    useCookieConsent,
    useCookieConsentInit,
    useErrorSnackbar,
} from 'ComponentLibrary'
import { datadogRum } from '@datadog/browser-rum'
import { WrappedFeatureFlagsProvider } from 'ClientPortal/src/context/FeatureFlagContext'
import { TaskSideDrawerProvider } from 'ComponentLibrary/src/context/TaskSideDrawerContext'
import { useFormQueryParams } from 'ClientPortal/src/router/useFormQueryParams'
import { useGetCurrentCustomer } from 'ClientPortal/src/api/Queries/customers'
import { useNoSSRWarnings } from 'ComponentLibrary/src/hooks/useNoSSRWarnings'
import { useResolvedLocale } from 'ClientPortal/src/i18n/useResolvedLocale'
import { usePersistMissingUserLocale } from 'ClientPortal/src/i18n/usePersistMissingUserLocale'
import { syncClientPortalCcmLocale } from 'ClientPortal/src/consent/clientPortalCcm'
import { RestrictedAppraiserGate } from 'ClientPortal/src/pages/RestrictedAppraiserModal'
import { getIsDatadogEnabled } from 'ClientPortal/src/common/environment'
import { QueryClientSingletonFactory } from 'ClientPortal/src/api/react-query-config'
import { handleError } from 'ClientPortal/src/ErrorHandling/errorHandlers'
import { signOut as amplifySignOut } from 'aws-amplify/auth'

type AppProps = { signOut: () => void }

let isDatadogInitialized = false

export function ClientPortalProviders({ children }: { children: ReactNode }) {
    return (
        <ErrorSnackbarProvider errorHandler={handleError}>
            <ClientPortalQueryProvider>{children}</ClientPortalQueryProvider>
        </ErrorSnackbarProvider>
    )
}

function ClientPortalQueryProvider({ children }: { children: ReactNode }) {
    useCookieConsentInit()
    const { handleError: showError } = useErrorSnackbar()
    const handleForbiddenErrorsOnly = (error: unknown) => {
        if (error instanceof ForbiddenError) {
            showError(error)
        }
    }
    const queryClient: QueryClient = QueryClientSingletonFactory.getInstance({
        handleError: handleForbiddenErrorsOnly,
        redirectToLogin: amplifySignOut,
    })

    return (
        <QueryClientProvider client={queryClient}>
            <DatadogInitializer />
            {children}
            <ErrorSnackbar
                anchorOrigin={{
                    horizontal: 'center',
                    vertical: 'top',
                }}
            />
            <ReactQueryDevtools initialIsOpen={false} buttonPosition="bottom-left" />
        </QueryClientProvider>
    )
}

function DatadogInitializer() {
    const { isEnabled: hasUserConsent } = useCookieConsent(SERVICES_NEEDING_CONSENT.DATADOG)
    const isEnabled = getIsDatadogEnabled()

    useEffect(() => {
        if (isEnabled && hasUserConsent) {
            initializeDatadog()
        }
    }, [hasUserConsent, isEnabled])

    return null
}

function initializeDatadog() {
    if (isDatadogInitialized) {
        return
    }

    datadogRum.init({
        applicationId: '6e6d006f-e84d-4c8e-9d2e-c8193cd8c1ce',
        clientToken: 'pub98de0b6e532586df0911f5a8b8860937',
        site: 'datadoghq.eu',
        service: 'client_portal',
        env: getEnv(),
        version: getFrontendVersion(),
        defaultPrivacyLevel: 'mask-user-input',
        sessionReplaySampleRate: 0,
        sessionSampleRate: 100,
        trackLongTasks: true,
        trackResources: true,
        trackUserInteractions: true,
    })
    isDatadogInitialized = true
}

function App({ signOut }: AppProps) {
    useNoSSRWarnings()
    const { user, isUserPending, isUserError } = useGetCurrentUser()
    const { customer } = useGetCurrentCustomer()
    const queryParams = useFormQueryParams()
    const { error: localeInitError, isReady: isLocaleReady, resolvedLocale } = useResolvedLocale({ user, isUserPending })

    usePersistMissingUserLocale({ user, isUserPending })
    useWelcomeRedirect({ user })
    useSentry({ user })
    useDatadog({ user, customer })
    useEffect(() => {
        if (!isLocaleReady) {
            return
        }

        document.documentElement.lang = resolvedLocale
        syncClientPortalCcmLocale(resolvedLocale)
    }, [isLocaleReady, resolvedLocale])

    if (localeInitError) {
        throw localeInitError
    }

    if (!isLocaleReady) {
        return null
    }

    return (
        <Suspense fallback={null}>
            <IAmContext.Provider value={{ signOut, user, isUserPending, isUserError }}>
                <SelectedCustomerProvider>
                    <WrappedFeatureFlagsProvider renderPending={() => null}>
                        <RestrictedAppraiserGate user={user}>
                            <TaskSideDrawerProvider
                                taskSideDrawerState={{
                                    openTaskId: queryParams.openTaskId || undefined,
                                    setOpenTaskId: queryParams.setOpenTaskId,
                                }}
                            >
                                <ScrollRestoration
                                    getKey={(location) => (location.pathname.includes('analysis') ? location.pathname : location.key)}
                                />
                                <Outlet />
                            </TaskSideDrawerProvider>
                        </RestrictedAppraiserGate>
                    </WrappedFeatureFlagsProvider>
                </SelectedCustomerProvider>
            </IAmContext.Provider>
        </Suspense>
    )
}

const useSentry = ({ user }: { user?: User }) => {
    useEffect(() => {
        if (user) {
            Sentry.setUser({ email: user.email, role: user.role })
        }
    }, [user])
}

const useDatadog = ({ user, customer }: { user?: User; customer?: Customer }) => {
    const { isEnabled: hasUserConsent } = useCookieConsent(SERVICES_NEEDING_CONSENT.DATADOG)
    const isEnabled = getIsDatadogEnabled()

    useEffect(() => {
        if (!isEnabled || !hasUserConsent) {
            return
        }

        initializeDatadog()
        if (user && customer) {
            datadogRum.setUser({ role: user.role, type: getCustomerType(customer) })
        }
    }, [isEnabled, hasUserConsent, user, customer])
}

// TODO this is a POC for Datadog and this mapping should be part of the customer object
const getCustomerType = (customer: Customer) => {
    if (
        [
            'a51584c2-2656-4c8b-92d9-9f6dec942329', // Patrizia
            '958aeaa0-7c26-47df-b455-a4e434310c8d', // Hansainvest
            '5c6c3e92-d382-459d-b549-3384b7836e22', // LHI
            '7acf2c8d-d196-4e75-bf57-88da1e85e432', // BNP
            '17f01a03-1b1f-4642-9863-e0395b27144d', // HPC
        ].includes(customer.uuid)
    ) {
        return 'fund'
    }

    if (
        [
            'f91fbfb2-3cd8-42ec-984b-b7c9ddd03c33', // Lebenswert
            'b401b64f-6eb4-4046-a3d6-3205041eba8f', // Engel & Völkers
            '385c429a-3d52-44aa-944a-a2c424664e1c', // LIDL
            '5267b956-d8ec-4d1d-b08c-8466fdc7db47', // Schwarz
        ].includes(customer.uuid)
    ) {
        return 'transactional'
    }

    if (['9f0e2c5c-c086-4e42-9e13-51847e4570b1'].includes(customer.uuid)) {
        return 'internal'
    }

    return 'unknown'
}

const useWelcomeRedirect = ({ user }: { user?: User }) => {
    const navigate = useNavigate()
    const isProfileComplete = (user: User) => user.role !== 'customer' || user.last_name

    useEffect(() => {
        try {
            const isNewLogin = sessionStorage.getItem('is-new-login') === 'true'
            if (user && isNewLogin) {
                sessionStorage.removeItem('is-new-login')
                if (!isProfileComplete(user)) {
                    void navigate('/welcome')
                }
            }
        } catch (error) {
            sendToSentry(error) // Probably user has blocked SessionStorage in their browser
        }
    }, [user, navigate])
}

export default App
